import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { ExpandMore, LocationOn } from '@mui/icons-material';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/theme';
import { isStudioScopedRole } from '../utils/roles';

interface Location {
  _id: string;
  studioName: string;
  branchName?: string | null;
  branchCode?: string | null;
  city?: string | null;
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHERE THE CHOSEN LOCATION IS REMEMBERED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Keyed by ACCOUNT, not stored as one global value.
 *
 * A single key was a session-bricking bug. The interceptor sends this as
 * `x-studio-id` on EVERY request, and the server correctly refuses a location
 * the caller has no grant for — so signing out of a brand owner with Yelahanka
 * selected and signing in as an unrelated single-studio owner made every
 * request in the new session return "Studio not found". Nothing on screen
 * explained it, and the only escape was clearing site data.
 *
 * Scoping the key to the account removes the class of bug rather than patching
 * the symptom: one login can no longer inherit another's selection, however
 * the session ended.
 */
const LEGACY_KEY = 'selected_studio_id';

/**
 * The account key, captured at LOGIN and never recomputed.
 *
 * Deliberately not `admin.id`. For a studio login that id is the STUDIO's, and
 * `verify-admin` reports whichever location is currently being viewed — so it
 * moved every time the location changed, taking the storage key with it. The
 * switcher and the dashboard then read two different keys and disagreed, each
 * exactly one selection behind the other.
 *
 * The backend no longer does that, but this key must not depend on it being
 * right. It is written once when the session starts and read verbatim
 * afterwards.
 */
const ACCOUNT_KEY = 'admin_account_key';

export function setAccountKey(value?: string | null): void {
  try {
    if (value) localStorage.setItem(ACCOUNT_KEY, String(value));
  } catch {
    /* storage disabled */
  }
}

function accountKey(): string | null {
  try {
    return localStorage.getItem(ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/**
 * "All locations" — the brand-wide view, and the DEFAULT for anyone who holds
 * more than one.
 *
 * Stored as a value rather than represented by an absent key, so "I chose
 * everything" and "I have not chosen yet" stay distinguishable. They behave
 * the same today; conflating them would make it impossible to tell later.
 */
export const ALL_LOCATIONS = '__all__';

export function locationKeyFor(_adminId?: string | null): string {
  // The argument is ignored — kept so call sites read naturally. The key comes
  // from the stable account key, never from anything that can change with the
  // selected location.
  const k = accountKey();
  return k ? `selected_studio_id:${k}` : LEGACY_KEY;
}

/** Called on login and logout — see AuthContext. */
/**
 * The location this account is looking at, or `ALL_LOCATIONS`.
 *
 * Read by the Dashboard to decide between the brand-wide report and the
 * ordinary single-studio view, and by the API client to decide whether to send
 * `x-studio-id` at all.
 */
export function getSelectedLocation(adminId?: string | null): string {
  try {
    return localStorage.getItem(locationKeyFor(adminId)) || ALL_LOCATIONS;
  } catch {
    return ALL_LOCATIONS;
  }
}

/**
 * Should this account START on "All locations"?
 *
 * Only when at least two of its locations carry a role of manager or above —
 * someone who manages one branch and only teaches at another has nothing to
 * aggregate, and `__all__` would refuse every write they make. Without
 * `roleByStudio` (older backend) the old rule stands: more than one location.
 */
export function shouldDefaultToAll(mine: any): boolean {
  const studios: any[] = mine?.studios ?? [];
  const rbs = mine?.roleByStudio;
  if (!rbs || typeof rbs !== 'object') return studios.length > 1;
  const strong = ['manager', 'studio_owner', 'brand_owner'];
  return (
    studios.filter((s) => strong.includes(rbs[String(s?._id)])).length >= 2
  );
}

export function clearStoredLocations(): void {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
    localStorage.removeItem(LEGACY_KEY);
    Object.keys(localStorage)
      .filter((k) => k.startsWith(`${LEGACY_KEY}:`))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // Private mode, or storage disabled. Nothing to clean up.
  }
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHICH LOCATION AM I LOOKING AT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Shown only to a studio-role user who actually holds more than one location —
 * which today means someone with a `brand_owner` grant. A studio owner with a
 * single location sees nothing, because a picker with one option is noise.
 *
 * The selection is sent as `x-studio-id` on every request; the guards validate
 * it against that person's grants and swap what `req.studio` resolves to, so
 * every existing studio-scoped page follows along without being touched.
 * Naming a location outside the grant returns 404, not 403 — see
 * resolveRequestStudio.
 *
 * ── WHY IT RELOADS ────────────────────────────────────────────────────────
 * Switching location invalidates essentially every figure on screen. This
 * panel has forty-odd pages, no global query cache, and each one fetches in
 * its own `useEffect`, so there is nothing to invalidate centrally — a
 * targeted refresh would mean touching all of them and would still leave
 * whichever one someone forgot showing another location's numbers. A reload
 * is blunt, but it cannot be partially wrong, and switching location is a
 * deliberate, occasional act rather than something done mid-task.
 */
const LocationSwitcher: React.FC = () => {
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);

  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const storageKey = locationKeyFor(admin?.id);
  const [selected, setSelected] = useState<string>(
    () => getSelectedLocation(admin?.id),
  );

  const load = useCallback(async () => {
    if (!isStudioRole) return;
    setLoading(true);
    try {
      const res: any = await apiService.getMyBrand();
      const studios: Location[] = res?.studios ?? [];
      setLocations(studios);

      // A stored id that is no longer granted would send a header the server
      // refuses on every request — every page would 404 at once. Drop it.
      const stored = localStorage.getItem(storageKey);
      const multi = studios.length > 1;
      if (stored === ALL_LOCATIONS && !multi) {
        // Down to a single location (a grant was removed), but "All" is still
        // stored and sent as `__all__` — which refuses every write. Drop it
        // and reload so every page fetches as the single location again.
        localStorage.removeItem(storageKey);
        window.location.reload();
      } else if (stored && stored !== ALL_LOCATIONS && !studios.some((s) => s._id === stored)) {
        // No longer granted: every request would be refused. Fall back to
        // "All" for someone with several locations, nothing for one.
        if (multi && shouldDefaultToAll(res)) localStorage.setItem(storageKey, ALL_LOCATIONS);
        else localStorage.removeItem(storageKey);
        setSelected(ALL_LOCATIONS);
        // It was being sent on every request until now. Reload so the pages
        // that already failed against it fetch again cleanly.
        window.location.reload();
      } else if (!stored && multi && !shouldDefaultToAll(res)) {
        // Several locations, but fewer than two where this person manages:
        // the default is the login's OWN location, not "All". Nothing is sent
        // for it today (the server already resolves the token's studio), so
        // record it only so the chip — and every page asking "am I on All?" —
        // says so too. Equivalent on the wire; no reload needed.
        const own = apiService.ownStudioId();
        if (own && studios.some((s) => String(s._id) === own)) {
          localStorage.setItem(storageKey, own);
          setSelected(own);
        }
      } else if (!stored && multi) {
        // A session restored from before login() recorded the default (or
        // before this feature shipped): the screen says "All locations" but
        // no header is sent, so only the home location loads. Record it and
        // reload once so every page fetches the aggregate.
        localStorage.setItem(storageKey, ALL_LOCATIONS);
        window.location.reload();
      }
    } catch {
      // No brand, or an older backend. No switcher, everything else unaffected.
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [isStudioRole, storageKey]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isStudioRole || locations.length <= 1) return null;

  const isAll = selected === ALL_LOCATIONS;
  const current = locations.find((l) => l._id === selected) ?? null;

  const label = (l: Location) => l.branchName || l.studioName;

  const choose = (value: string) => {
    setAnchor(null);
    // Compare with what is actually STORED, not the displayed default: with
    // nothing stored the chip shows "All" while no header is sent, and picking
    // "All" must then take effect rather than be ignored.
    if (value === localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, value);
    // Everything on screen was about the previous scope. See the note above.
    window.location.reload();
  };

  return (
    <>
      <Chip
        icon={loading ? undefined : <LocationOn sx={{ fontSize: '16px !important' }} />}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {loading ? <CircularProgress size={12} /> : null}
            <span>{isAll ? 'All locations' : current ? label(current) : 'Location'}</span>
            <ExpandMore sx={{ fontSize: 16 }} />
          </Box>
        }
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          cursor: 'pointer',
          fontWeight: 600,
          bgcolor: colors.primaryTint,
          border: `1px solid ${colors.divider}`,
        }}
      />

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}
        >
          Your locations
        </Typography>
        <Divider />

        {/* The brand-wide view: totals across every location. Default. */}
        <MenuItem selected={isAll} onClick={() => choose(ALL_LOCATIONS)}>
          <ListItemText
            primary="All locations"
            secondary={`Combined across ${locations.length}`}
          />
        </MenuItem>
        <Divider />

        {locations.map((l) => (
          <MenuItem
            key={l._id}
            selected={l._id === current?._id}
            onClick={() => choose(l._id)}
          >
            <ListItemText
              primary={label(l)}
              secondary={[l.branchCode, l.city].filter(Boolean).join(' · ') || undefined}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LocationSwitcher;
